import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { getAdminSuccessStories, createSuccessStory, updateSuccessStory, deleteSuccessStory } from '../../api/client';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, GripVertical, Eye, Upload, Quote } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    metric: '',
    quote: '',
    author: '',
    image: '',
    order: 0,
    visible: true
  });

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const data = await getAdminSuccessStories();
      setStories(data.stories || []);
    } catch (error) {
      toast.error('Failed to load success stories');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        headers: { 
          'Content-Type': 'multipart/form-data', 
          'Authorization': `Bearer ${token}` 
        }
      });
      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, image: fileUrl });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const openDialog = (story = null) => {
    if (story) {
      setEditingStory(story);
      setFormData({
        title: story.title || '',
        metric: story.metric || '',
        quote: story.quote || '',
        author: story.author || '',
        image: story.image || '',
        order: story.order || 0,
        visible: story.visible !== false
      });
    } else {
      setEditingStory(null);
      setFormData({
        title: '',
        metric: '',
        quote: '',
        author: '',
        image: '',
        order: stories.length,
        visible: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.quote.trim()) {
      toast.error('Title and quote are required');
      return;
    }

    try {
      if (editingStory) {
        await updateSuccessStory(editingStory.id, formData);
        toast.success('Story updated!');
      } else {
        await createSuccessStory(formData);
        toast.success('Story created!');
      }
      setDialogOpen(false);
      fetchStories();
    } catch (error) {
      toast.error('Failed to save story');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this success story?')) return;
    
    try {
      await deleteSuccessStory(id);
      toast.success('Story deleted!');
      fetchStories();
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  const toggleVisibility = async (story) => {
    try {
      await updateSuccessStory(story.id, { ...story, visible: !story.visible });
      fetchStories();
      toast.success(story.visible ? 'Story hidden' : 'Story visible');
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Success Stories</h2>
            <p className="text-gray-600 mt-1">Manage testimonials and success stories</p>
          </div>
          <Button onClick={() => openDialog()} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
            <Plus size={16} />
            Add Story
          </Button>
        </div>

        {stories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 mb-4">No success stories added yet</p>
              <Button onClick={() => openDialog()} variant="outline">
                <Plus size={16} className="mr-2" />
                Add your first story
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {stories.map((story) => (
              <Card key={story.id} className={`${!story.visible ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-400 cursor-move mt-2">
                      <GripVertical size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{story.title}</h3>
                          {story.metric && (
                            <p className="text-2xl font-bold text-green-600 my-1">{story.metric}</p>
                          )}
                        </div>
                        {story.image && (
                          <img src={story.image} alt={story.title} className="w-16 h-16 rounded-lg object-cover" />
                        )}
                      </div>
                      
                      <div className="mt-2 flex items-start gap-2">
                        <Quote size={16} className="text-gray-400 mt-1 flex-shrink-0" />
                        <p className="text-gray-600 italic text-sm line-clamp-2">{story.quote}</p>
                      </div>
                      
                      {story.author && (
                        <p className="text-sm text-gray-500 mt-2">— {story.author}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleVisibility(story)}
                        title={story.visible ? 'Hide' : 'Show'}
                      >
                        <Eye size={16} className={story.visible ? 'text-green-600' : 'text-gray-400'} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDialog(story)}>
                        <Edit2 size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(story.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingStory ? 'Edit Success Story' : 'Add Success Story'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Halal Cosmetics Expansion"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Key Metric</Label>
                <Input
                  value={formData.metric}
                  onChange={(e) => setFormData({ ...formData, metric: e.target.value })}
                  placeholder="e.g., +200% Sales or $2M Raised"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Quote / Testimonial *</Label>
                <Textarea
                  value={formData.quote}
                  onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                  placeholder="The testimonial or success story quote..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Author / Attribution</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="e.g., CEO, Company Name"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Image (optional)</Label>
                <div className="mt-1 space-y-2">
                  <label className="block">
                    <div className={`
                      flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed 
                      rounded-lg cursor-pointer transition-colors
                      ${uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'}
                    `}>
                      {uploading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-gray-500">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={16} className="text-gray-500" />
                          <span className="text-sm text-gray-600">Upload Image</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="w-24 h-24 rounded-lg object-cover" />
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Visible on website</Label>
                <Switch
                  checked={formData.visible}
                  onCheckedChange={(checked) => setFormData({ ...formData, visible: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                {editingStory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
